/**
 * appConstants.ts — App-wide constants for authentication, routing, and API paths.
 *
 * These constants are used across the application for user group/role checks,
 * CSRF token handling, backend URL paths, and navigation route paths.
 */

// {{{ user groups — match Django auth group names
/** Django auth group name for teachers. Mirror of TEACHERS_GROUP in bmt/permissions.py. */
export const USER_GROUP_TEACHERS = 'Teachers'

/** Django auth group name for students. Mirror of STUDENTS_GROUP in bmt/permissions.py. */
export const USER_GROUP_STUDENTS = 'Students'
// }}}

// {{{ route roles — used in router meta for access control
/** Role string for teacher-only or teacher-accessible routes. */
export const ROLE_TEACHER = 'teacher'

/** Role string for student-only or student-accessible routes. */
export const ROLE_STUDENT = 'student'

/** Role string for admin-only routes. */
export const ROLE_ADMIN = 'admin'
// }}}

// {{{ CSRF token handling — must match Django's default CSRF middleware settings
/** Cookie name for Django's CSRF token. Matches Django's default CSRF_COOKIE_NAME setting. */
export const CSRF_COOKIE_NAME = 'csrftoken'

/** HTTP header name for sending the CSRF token with requests. Matches Django's default CSRF_HEADER_NAME setting. */
export const CSRF_HEADER_NAME = 'X-CSRFToken'
// }}}

// {{{ backend API paths
/** GraphQL API endpoint path (used by Apollo client and sendBeacon). Must match backend/urls.py. */
export const GRAPHQL_URL = '/backend/graphql/'

/** Backend URL path for stopping impersonation. Must match backend/urls.py impersonate path. */
export const BACKEND_IMPERSONATE_STOP_URL = '/backend/impersonate/stop/'

/** Backend URL path prefix for starting impersonation (append `/${userId}/`). Must match backend/urls.py impersonate path. */
export const BACKEND_IMPERSONATE_BASE_URL = '/backend/impersonate'
// }}}

// {{{ navigation route paths
/** Login page route path. */
export const ROUTE_LOGIN = '/login'

/** Practice page route path (default landing for students). */
export const ROUTE_PRACTICE = '/practice'

/** Recordings page route path (default landing for teachers). */
export const ROUTE_RECORDINGS = '/recordings'

/** Legacy practice page route path. */
export const ROUTE_LEGACY = '/legacy'

/** Student's own recordings page route path. */
export const ROUTE_MY_RECORDINGS = '/my-recordings'

/** Customize page route path (teacher). */
export const ROUTE_CUSTOMIZE = '/customize'

/** Students list page route path (teacher). */
export const ROUTE_STUDENTS = '/students'

/** Student recordings page route path (teacher). */
export const ROUTE_STUDENT_RECORDINGS = '/student-recordings'

/** Admin page route path. */
export const ROUTE_ADMIN = '/admin'
// }}}

// {{{ well-known usernames — backend-defined special users
/** Username for the system/root user whose content is shared with all teachers. Mirror of ROOT_USERNAME in bmt/permissions.py. */
export const ROOT_USERNAME = 'root'

/** Username for the public demo/test user. */
export const PUBLIC_USERNAME = 'public'
// }}}

// {{{ environment names — derived from hostname-to-environment mapping
/** Environment string for production hosts. */
export const ENV_PRODUCTION = 'production'

/** Environment string for staging hosts. */
export const ENV_STAGING = 'staging'

/** Environment string for development hosts (localhost, private IPs). */
export const ENV_DEVELOPMENT = 'development'
// }}}

// {{{ API boundary sentinels
/**
 * Sentinel value meaning "no filter" / "no existing ID". Mirror of NO_FILTER_SENTINEL in bmt/constants.py.
 * Used in GraphQL query variables to indicate no selection (e.g., parashah filter, voice style).
 * Also used as a fallback ID in create mutations to signal "create new" rather than "update existing".
 */
export const NO_FILTER_SENTINEL = '-1'
// }}}

// {{{ external URLs
/** Google Forms URL for the beta tester signup. */
export const BETA_SIGNUP_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfWqOqW4fcQ02RdjpYKqTuj7VibH8jTSGY7Kuy-0suIZ-oU0g/viewform?usp=sf_link'

/** Sefaria API base URL for fetching Torah text. */
export const SEFARIA_API_BASE_URL = 'https://www.sefaria.org/api/texts'
// }}}

// {{{ Torah column analysis
/** How often the client asks whether the server has finished analyzing an uploaded column photo. */
export const COLUMN_ANALYSIS_POLL_INTERVAL_MS = 2000

/** How long the client waits for the analysis before giving up (the worker itself needs 10 to 20 seconds). */
export const COLUMN_ANALYSIS_MAX_WAIT_MS = 120_000

/** Shown when the analysis did not finish within COLUMN_ANALYSIS_MAX_WAIT_MS. */
export const COLUMN_ANALYSIS_TIMEOUT_MESSAGE = 'Analyzing the photo is taking too long. Please try again later.'
// }}}
